import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2, FileText, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import Seo from "@/components/Seo";

export default function Contact() {
  const { contact: ct } = useContent();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. I'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-[#FBF9F4] border border-[#E7DFD0] rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-[var(--brand-primary)] font-medium";

  return (
    <div className="pt-20">
      <Seo
        title="Contact Kim Kornachuk | Work With Me"
        description="Get in touch about B2B marketing leadership roles, GTM consulting, advisory work, or speaking. Based in Los Angeles, working globally."
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Kim Kornachuk",
          url: "https://kimkornachuk.com/contact",
        }}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 orange-wash opacity-50 pointer-events-none scale-x-[-1]" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-24 relative">
          <div className="grid md:grid-cols-12 gap-16">
            <div className="md:col-span-5">
              <Reveal>
                <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-6 t-heading">{ct.kicker}</p>
                <h1 className="font-display text-6xl sm:text-8xl leading-[0.92] tracking-tight whitespace-pre-line t-heading">
                  {ct.heading}
                </h1>
                <p className="mt-8 text-lg leading-relaxed text-[var(--brand-muted)] font-medium t-body">
                  {ct.description}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/book" data-testid="contact-book-cta" className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-[var(--brand-cream)] px-6 py-3 rounded-full font-bold uppercase tracking-[0.1em] text-xs transition-all hover:bg-[var(--brand-primary-dark)] shadow-[0_10px_30px_-10px_rgba(225,37,27,0.6)]">
                    <CalendarDays size={16} /> Book a call
                  </Link>
                  <Link to="/resume" data-testid="contact-resume-cta" className="inline-flex items-center gap-2 border border-[var(--brand-heading)] text-[var(--brand-heading)] px-6 py-3 rounded-full font-bold uppercase tracking-[0.1em] text-xs transition-all hover:bg-[var(--brand-heading)] hover:text-[var(--brand-cream)]">
                    <FileText size={16} /> Get my resume
                  </Link>
                </div>
                <div className="mt-12 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-[var(--brand-primary)] p-3 rounded-2xl text-[var(--brand-cream)]"><Mail size={20} /></div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--brand-muted)]">Email</p>
                      <p className="text-[var(--brand-heading)] font-bold">{ct.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="bg-[var(--brand-primary)] p-3 rounded-2xl text-[var(--brand-cream)]"><MapPin size={20} /></div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--brand-muted)]">Based in</p>
                      <p className="text-[var(--brand-heading)] font-bold">{ct.location}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="md:col-span-7">
              <Reveal delay={0.15}>
                <form onSubmit={submit} data-testid="contact-form" className="bg-[#FBF9F4] border border-[#E7DFD0] rounded-3xl shadow-[0_8px_30px_-14px_rgba(28,24,21,0.16)] p-8 sm:p-10 space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[var(--brand-muted)] mb-2">Name</label>
                      <input data-testid="contact-name-input" value={form.name} onChange={update("name")} required className={field} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[var(--brand-muted)] mb-2">Email</label>
                      <input data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} required className={field} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[var(--brand-muted)] mb-2">Subject</label>
                    <input data-testid="contact-subject-input" value={form.subject} onChange={update("subject")} className={field} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[var(--brand-muted)] mb-2">Message</label>
                    <textarea data-testid="contact-message-input" rows={6} value={form.message} onChange={update("message")} required className={field + " resize-none"} />
                  </div>
                  <button data-testid="contact-submit-button" type="submit" disabled={loading} className="group inline-flex items-center gap-2 bg-[var(--brand-primary)] text-[var(--brand-cream)] px-7 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm transition-all hover:bg-[var(--brand-primary-dark)] disabled:opacity-60 shadow-[0_10px_30px_-10px_rgba(225,37,27,0.6)]">
                    {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Send message <Send size={18} className="transition-transform group-hover:translate-x-1" /></>}
                  </button>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
